import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API, getAuthHeaders } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  User, 
  Mail, 
  Calendar, 
  FileText, 
  Instagram, 
  Twitter, 
  Youtube, 
  Music,
  ChevronDown,
  ChevronUp,
  Image,
  DollarSign,
  X,
  ExternalLink
} from 'lucide-react';

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [viewingSubmission, setViewingSubmission] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchMySubmissions();
    }
  }, [user, authLoading, navigate]);

  const fetchMySubmissions = async () => {
    try {
      const response = await axios.get(`${API}/submissions/my`, {
        headers: getAuthHeaders()
      });
      setSubmissions(response.data);
    } catch (error) {
      // Error fetching submissions
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412] flex items-center justify-center">
        <div className="text-[#A89B95]">Loading...</div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-500/20 text-green-300';
      case 'pending_verification':
        return 'bg-yellow-500/20 text-yellow-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (submissionId) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
            My Dashboard
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit']">
            Welcome back, {user.full_name}
          </p>
        </div>

        {/* User Info Card */}
        <Card className="bg-[#2C221F] border-[#3D302C] mb-8">
          <CardHeader>
            <CardTitle className="text-[#FDFBF7] font-['Playfair_Display']">Profile Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center gap-3">
                <User className="text-[#D97736]" size={20} />
                <div>
                  <div className="text-[#A89B95] text-sm">Name</div>
                  <div className="text-[#FDFBF7]">{user.full_name}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="text-[#D97736]" size={20} />
                <div>
                  <div className="text-[#A89B95] text-sm">Email</div>
                  <div className="text-[#FDFBF7]">{user.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="text-[#D97736]" size={20} />
                <div>
                  <div className="text-[#A89B95] text-sm">Role</div>
                  <div className="text-[#FDFBF7] capitalize">{user.role}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submissions */}
        <div>
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7] mb-6">My Submissions</h2>
          
          {loading ? (
            <div className="text-center text-[#A89B95] py-12" data-testid="loading-submissions">
              Loading submissions...
            </div>
          ) : submissions.length === 0 ? (
            <Card className="bg-[#2C221F] border-[#3D302C]">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 text-[#3D302C] mx-auto mb-4" />
                <p className="text-[#A89B95] mb-4" data-testid="no-submissions">
                  You haven't submitted any applications yet.
                </p>
                <Button 
                  onClick={() => navigate('/submit')}
                  className="bg-[#D97736] hover:bg-[#F08F4D] text-white"
                >
                  Submit Your Application
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission, index) => (
                <Card 
                  key={submission.id || index} 
                  className="bg-[#2C221F] border-[#3D302C] hover:border-[#D97736]/50 transition-colors overflow-hidden"
                  data-testid={`submission-card-${index}`}
                >
                  <CardContent className="p-0">
                    {/* Header - Always Visible */}
                    <div 
                      className="p-6 cursor-pointer"
                      onClick={() => toggleExpand(submission.id)}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#FDFBF7]">
                              {submission.full_name}
                            </h3>
                            <Badge className={getStatusColor(submission.status)} data-testid={`status-badge-${index}`}>
                              {submission.status}
                            </Badge>
                          </div>
                          <p className="text-[#A89B95] text-sm mb-2">
                            Type: <span className="text-[#FDFBF7] capitalize">{submission.submission_type}</span>
                          </p>
                          {submission.bio_text && (
                            <p className="text-[#A89B95] text-sm line-clamp-2">{submission.bio_text}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-[#A89B95] text-sm">
                            {formatDate(submission.created_at)}
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-[#D97736] hover:bg-[#D97736]/10"
                            data-testid={`expand-btn-${index}`}
                          >
                            {expandedSubmission === submission.id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {expandedSubmission === submission.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="border-t border-[#3D302C]"
                        >
                          <div className="p-6 bg-[#1A1412]">
                            {/* Bio Text */}
                            {submission.bio_text && (
                              <div className="mb-6">
                                <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                  <FileText size={16} /> Bio
                                </h4>
                                <p className="text-[#FDFBF7] bg-[#2C221F] p-4 rounded-lg whitespace-pre-wrap">
                                  {submission.bio_text}
                                </p>
                              </div>
                            )}

                            {/* Contact Info */}
                            <div className="mb-6">
                              <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                <Mail size={16} /> Contact Information
                              </h4>
                              <div className="bg-[#2C221F] p-4 rounded-lg">
                                <p className="text-[#FDFBF7]">
                                  <span className="text-[#A89B95]">Email:</span> {submission.email}
                                </p>
                              </div>
                            </div>

                            {/* Social Media */}
                            {submission.social_media && Object.keys(submission.social_media).some(key => submission.social_media[key]) && (
                              <div className="mb-6">
                                <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                  <ExternalLink size={16} /> Social Media Links
                                </h4>
                                <div className="bg-[#2C221F] p-4 rounded-lg space-y-2">
                                  {submission.social_media.instagram && (
                                    <a 
                                      href={submission.social_media.instagram} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-[#FDFBF7] hover:text-[#D97736] transition-colors"
                                    >
                                      <Instagram size={18} className="text-pink-500" />
                                      {submission.social_media.instagram}
                                    </a>
                                  )}
                                  {submission.social_media.twitter && (
                                    <a 
                                      href={submission.social_media.twitter} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-[#FDFBF7] hover:text-[#D97736] transition-colors"
                                    >
                                      <Twitter size={18} className="text-blue-400" />
                                      {submission.social_media.twitter}
                                    </a>
                                  )}
                                  {submission.social_media.youtube && (
                                    <a 
                                      href={submission.social_media.youtube} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-[#FDFBF7] hover:text-[#D97736] transition-colors"
                                    >
                                      <Youtube size={18} className="text-red-500" />
                                      {submission.social_media.youtube}
                                    </a>
                                  )}
                                  {submission.social_media.spotify && (
                                    <a 
                                      href={submission.social_media.spotify} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-[#FDFBF7] hover:text-[#D97736] transition-colors"
                                    >
                                      <Music size={18} className="text-green-500" />
                                      {submission.social_media.spotify}
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Photos */}
                            {submission.photos && submission.photos.length > 0 && (
                              <div className="mb-6">
                                <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                  <Image size={16} /> Photos ({submission.photos.length})
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {submission.photos.map((photoId, i) => (
                                    <div 
                                      key={photoId || i}
                                      className="aspect-square bg-[#2C221F] rounded-lg flex items-center justify-center text-[#A89B95]"
                                    >
                                      <Image size={32} />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Music Samples */}
                            {submission.music_samples && submission.music_samples.length > 0 && (
                              <div className="mb-6">
                                <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                  <Music size={16} /> Music Samples ({submission.music_samples.length})
                                </h4>
                                <div className="bg-[#2C221F] p-4 rounded-lg">
                                  <p className="text-[#A89B95]">
                                    {submission.music_samples.length} audio file(s) uploaded
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Payment Status */}
                            <div className="mb-6">
                              <h4 className="text-[#CFB53B] font-semibold mb-2 flex items-center gap-2">
                                <DollarSign size={16} /> Payment Status
                              </h4>
                              <div className="bg-[#2C221F] p-4 rounded-lg flex items-center gap-4">
                                <Badge className={getPaymentStatusColor(submission.payment_status)}>
                                  {submission.payment_status === 'pending_verification' 
                                    ? 'Pending Verification' 
                                    : submission.payment_status || 'N/A'}
                                </Badge>
                                {submission.payment_method && (
                                  <span className="text-[#A89B95] text-sm">
                                    via {submission.payment_method}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Timestamps */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              <div className="bg-[#2C221F] p-4 rounded-lg">
                                <span className="text-[#A89B95]">Submitted:</span>
                                <span className="text-[#FDFBF7] ml-2">{formatDate(submission.created_at)}</span>
                              </div>
                              {submission.updated_at && submission.updated_at !== submission.created_at && (
                                <div className="bg-[#2C221F] p-4 rounded-lg">
                                  <span className="text-[#A89B95]">Last Updated:</span>
                                  <span className="text-[#FDFBF7] ml-2">{formatDate(submission.updated_at)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
